import type { Achievement, ChangelogPayload, OfflineReport, RoadSegmentPayload } from '@/network/api';
import type { HeroQuest, Town } from '@/game/types';
import type { HeroSheetTab } from '@/modules/hero-sheet/components/HeroSheetModal';
import type { ActiveWorldEvent } from '@/world-events';

export type ChangelogOpen = { payload: ChangelogPayload; serverVersion?: string };

export interface SessionStoreState {
  wsConnected: boolean;
  wsEverConnected: boolean;
  connectionError: string | null;
  heroQuests: HeroQuest[];
  achievements: Achievement[];
  heroSheetOpen: boolean;
  heroSheetInitialTab: HeroSheetTab;
  changelogOpen: ChangelogOpen | null;
  offlineReport: OfflineReport | null;
  needsName: boolean;
  activeWorldEvent: ActiveWorldEvent | null;
  towns: Town[];
  roadNetwork: Array<Array<{ x: number; y: number }>>;
  roadSegments: RoadSegmentPayload[];
}

export type SessionAction =
  | { type: 'SET_WS_CONNECTED'; connected: boolean }
  | { type: 'SET_CONNECTION_ERROR'; error: string | null }
  | { type: 'SET_HERO_QUESTS'; quests: HeroQuest[] }
  | { type: 'UPDATE_QUEST_PROGRESS'; questId: number; progress: number }
  | { type: 'COMPLETE_QUEST'; questId: number }
  | { type: 'SET_ACHIEVEMENTS'; achievements: Achievement[] }
  | { type: 'SET_HERO_SHEET_OPEN'; open: boolean; tab?: HeroSheetTab }
  | { type: 'SET_CHANGELOG'; changelog: ChangelogOpen | null }
  | { type: 'SET_OFFLINE_REPORT'; report: OfflineReport | null }
  | { type: 'SET_NEEDS_NAME'; needsName: boolean }
  | { type: 'SET_ACTIVE_WORLD_EVENT'; event: ActiveWorldEvent | null }
  | { type: 'SET_TOWNS'; towns: Town[] }
  | { type: 'SET_ROAD_NETWORK'; network: Array<Array<{ x: number; y: number }>> }
  | { type: 'SET_ROAD_SEGMENTS'; segments: RoadSegmentPayload[] };

export const initialSessionStore: SessionStoreState = {
  wsConnected: false,
  wsEverConnected: false,
  connectionError: null,
  heroQuests: [],
  achievements: [],
  heroSheetOpen: false,
  heroSheetInitialTab: 'stats',
  changelogOpen: null,
  offlineReport: null,
  needsName: false,
  activeWorldEvent: null,
  towns: [],
  roadNetwork: [],
  roadSegments: [],
};

export function sessionReducer(state: SessionStoreState, action: SessionAction): SessionStoreState {
  switch (action.type) {
    case 'SET_WS_CONNECTED':
      return {
        ...state,
        wsConnected: action.connected,
        wsEverConnected: action.connected ? true : state.wsEverConnected,
      };
    case 'SET_CONNECTION_ERROR':
      return { ...state, connectionError: action.error };
    case 'SET_HERO_QUESTS':
      return { ...state, heroQuests: action.quests };
    case 'UPDATE_QUEST_PROGRESS':
      return {
        ...state,
        heroQuests: state.heroQuests.map((hq) =>
          hq.questId === action.questId ? { ...hq, progress: action.progress } : hq,
        ),
      };
    case 'COMPLETE_QUEST':
      return {
        ...state,
        heroQuests: state.heroQuests.map((hq) =>
          hq.questId === action.questId ? { ...hq, status: 'completed' as const } : hq,
        ),
      };
    case 'SET_ACHIEVEMENTS':
      return { ...state, achievements: action.achievements };
    case 'SET_HERO_SHEET_OPEN':
      return {
        ...state,
        heroSheetOpen: action.open,
        heroSheetInitialTab: action.tab ?? state.heroSheetInitialTab,
      };
    case 'SET_CHANGELOG':
      return { ...state, changelogOpen: action.changelog };
    case 'SET_OFFLINE_REPORT':
      return { ...state, offlineReport: action.report };
    case 'SET_NEEDS_NAME':
      return { ...state, needsName: action.needsName };
    case 'SET_ACTIVE_WORLD_EVENT':
      return { ...state, activeWorldEvent: action.event };
    case 'SET_TOWNS':
      return { ...state, towns: action.towns };
    case 'SET_ROAD_NETWORK':
      return { ...state, roadNetwork: action.network };
    case 'SET_ROAD_SEGMENTS':
      return { ...state, roadSegments: action.segments };
    default:
      return state;
  }
}
